import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { IRelatedProductsOptions } from '@df/catalog-ui/related-products/related-products.service';
import { App } from '@df/core/app';
import { ZippyModule } from '@df/legacy/ui/zippy/zippy.module';
import { ZippyService } from '@df/legacy/ui/zippy/zippy.service';
import { KlevuSearch } from '@df/module-klevu-search/klevu-interfaces';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { SizeClassDirective } from '@df/ui/common/size-class.directive';
import { DfUiModule } from '@df/ui/df-ui.module';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent } from '@icms/core/content';
import sortBy from 'lodash-es/sortBy';
import { ScrollNavTargetDirective } from 'projects/app-default/src/app/ui/scroll-nav/scroll-nav-target.directive';
import { ProductViewLayoutComponent } from '../../../product/view-layout/product-view-layout.component';
import { RelatedProductsComponent } from '../../../related-products/related-products.component';
import { klevuRecomendationsFileds } from './klevu-recommendations.config';
import { PRODUCT_CAROSUEL_CONFIG } from './product-carousel.config';
import { IPageComponentProductCarouselData, IPageComponentProductCarouselSection } from './product-carousel.interface';

let uid = 0;
@Component({
    selector: 'page-component-product-carousel',
    templateUrl: './product-carousel.html',
    styleUrls: ['./product-carousel.scss'],
    standalone: true,
    imports: [
        SizeClassDirective,
        ICmsClientModule,
        ScrollNavTargetDirective,
        TranslationModule,
        DfUiModule,
        ZippyModule,
        HrefComponent,
        RelatedProductsComponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
@ICmsComponent<PageComponentProductCarouselComponent>(PRODUCT_CAROSUEL_CONFIG)
export class PageComponentProductCarouselComponent extends UiComponent implements NICmsContentComponent.IComponent, OnInit {
    @ICmsContent({
        type: 'component'
    })
    @Input()
    component!: IPageComponentProductCarouselData;

    private readonly _uid = `productCarouselComponent${uid++}`;

    printedSections: IPageComponentProductCarouselSection[] = [];

    public productViewLayoutComponent?: ProductViewLayoutComponent;

    constructor(
        private zippyService: ZippyService,
        private app: App
    ) {
        super();

        this.subscriptions.add(
            this.app.client.obs.breakpoint.subscribe(() => {
                // have to rerender cause cms config can be different for breakpoints
                this.printedSections = [];
                this.detectChanges();
            })
        );

        this.subscriptions.add(
            this.productViewLayoutComponent?.productChange.subscribe(() => {
                this.resetSelectedSection();
                this.detectChanges();
            })
        );
    }

    get product() {
        return this.productViewLayoutComponent?.product;
    }

    get uid(): string {
        return this._uid;
    }

    /**
     * If componentn uses multiple sections
     */
    get hasManySections(): boolean {
        return this.printedSections.length > 1;
    }

    private resetSelectedSection(): void {
        if (this.printedSections.length) {
            this.zippyService.open(this.zippyService.toZippyId(this.getZippyId(this.printedSections[0])));
        }
    }

    protected getSource(section: IPageComponentProductCarouselSection) {
        return KlevuSearch.contextRecommendationsBasketEngineId === section.engine ? 'basket' : this.product;
    }

    protected options(section: IPageComponentProductCarouselSection): IRelatedProductsOptions {
        const klevuOptions = {};

        if (KlevuSearch.isKlevuEngine(section.engine)) {
            klevuRecomendationsFileds().forEach(field => {
                if (section[field.key]) {
                    klevuOptions[field.key] = section[field.key];
                }
            });
        }

        if (KlevuSearch.contextRecommendationsBasketEngineId === section.engine) {
            return {
                engines: [{ id: KlevuSearch.contextRecommendationsBasketEngineId, options: { strategyId: section.klevuRecommendationId } }]
            };
        }

        return {
            engines: [
                {
                    id: section.engine,
                    options: {
                        // manual-engine
                        ids: section.product_ids,
                        // category-engine
                        categoryIds: section.category_ids,
                        // klevu-engine
                        ...klevuOptions
                    }
                }
            ]
        };
    }

    /*
     * Method used by related-products component,
     * sets section related to it as printed (allowed to be displayed)
     * if related-products component found relevant products to display.
     * We do not display section if it does not have any products to present.
     */
    protected printSection(section: IPageComponentProductCarouselSection, event: boolean): void {
        if (event) {
            if (!this.printedSections.includes(section)) {
                this.printedSections.push(section);

                if (this.printedSections.length > 1) {
                    this.printedSections = sortBy(this.printedSections, o => this.component.sections?.findIndex(e => e === o));
                }

                this.zippyService.open(this.zippyService.toZippyId(this.getZippyId(this.printedSections[0])));
                this.detectChanges();
            }
        } else {
            if (this.printedSections.includes(section)) {
                this.printedSections = this.printedSections.filter(i => i !== section);
                this.detectChanges();
            }
        }
    }

    protected getZippyId(section: IPageComponentProductCarouselSection) {
        return ['product_carousel' + this.uid, section.title];
    }

    protected isSectionPrinted(section: IPageComponentProductCarouselSection): boolean {
        return this.printedSections?.includes(section);
    }
}
